// CouncilComposer.jsx — structured composer for Council v2.
//
// Replaces the inline textarea block in CouncilView. Owns the question, the
// attachment chips (with honest truncation/metadata notes), the mode selector
// (default rebuttal) and the convoke/cancel/reset actions. Selection of models
// lives in CouncilModelPicker; config collapse lives in CouncilConfig.

import { FileText, Image as ImageIcon, Loader2, Paperclip, Quote, Send, Sparkles, X } from 'lucide-react';
import { describeAttachments } from './councilAttachments.js';

const MODES = [
  { id: 'rebuttal', label: 'Rebuttal', hint: 'Opiniões → revisão anônima → réplicas → síntese' },
  { id: 'essential', label: 'Essential', hint: 'Opiniões → revisão anônima → síntese' },
];

export default function CouncilComposer({
  brief = {},
  onBriefChange,
  query,
  onQueryChange,
  attachments,
  dragOver,
  uploadError,
  removeAttachment,
  openFilePicker,
  dropZoneHandlers,
  fileInputRef,
  fileInputHandlers,
  mode,
  onModeChange,
  isRunning,
  canConvoke,
  onConvoke,
  onCancel,
  onReset,
  phase,
}) {
  const described = describeAttachments(attachments);

  return (
    <section
      aria-label="Compor questão"
      {...dropZoneHandlers}
      className={
        'relative flex flex-col gap-4 rounded-2xl border bg-apolo-surface1 p-3 transition lg:p-5 '
        + (dragOver ? 'border-apolo-accent ring-2 ring-apolo-accent/30 bg-apolo-accentMuted/30' : 'border-apolo-borderSubtle')
      }
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-apolo-accent bg-apolo-bg/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-1 text-apolo-accent">
            <Paperclip size={20} aria-hidden="true" strokeWidth={2.25} />
            <span className="text-sm font-medium">Solte aqui pra anexar</span>
            <span className="text-[10px] text-apolo-textTertiary">Arquivos de texto entram inline (truncados); imagens/PDF ficam só como metadados</span>
          </div>
        </div>
      )}
      <input ref={fileInputRef} type="file" multiple {...fileInputHandlers} className="hidden" aria-hidden="true" />

      <label className="flex flex-col gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-apolo-textSecondary">
          <Quote size={12} aria-hidden="true" />
          Pergunta para o conselho
        </span>
        <textarea
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          rows={3}
          placeholder="Devo escolher microservices ou modular monolith pra esse SaaS?"
          disabled={isRunning}
          aria-label="Pergunta para o conselho"
          className="resize-y rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none disabled:opacity-60"
        />
      </label>

      <details className="text-sm text-apolo-textSecondary">
        <summary className="cursor-pointer">Contexto, restrições e critérios</summary>
        <div className="mt-3 grid gap-3">
          {[['context', 'Contexto do projeto'], ['constraints', 'Restrições'], ['criteria', 'Critérios de decisão']].map(([key, label]) => (
            <label key={key} className="grid gap-1">
              {label}
              <textarea rows={2} disabled={isRunning} value={brief[key] || ''}
                onChange={(event) => onBriefChange?.({ ...brief, [key]: event.target.value })}
                className="rounded border border-apolo-border bg-apolo-surface2 p-2 text-apolo-textPrimary" />
            </label>
          ))}
        </div>
      </details>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-apolo-textTertiary">Modo de deliberação</legend>
        <div role="radiogroup" aria-label="Modo de deliberação" className="flex flex-wrap gap-1.5">
          {MODES.map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={isRunning}
                onClick={() => onModeChange(m.id)}
                title={m.hint}
                className={
                  'inline-flex flex-col items-start rounded-lg border px-2.5 py-1.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing '
                  + (active ? 'border-apolo-accent bg-apolo-surface2' : 'border-apolo-borderSubtle hover:bg-apolo-surface2')
                }
              >
                <span className="text-[11.5px] font-semibold text-apolo-textPrimary">{m.label}</span>
                <span className="text-[9.5px] text-apolo-textTertiary">{m.hint}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-apolo-textTertiary">
            <Paperclip size={10} aria-hidden="true" strokeWidth={2.25} />
            Anexos
            {attachments.length > 0 && <span className="rounded-full bg-apolo-surface3 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-apolo-textSecondary">{attachments.length}</span>}
          </span>
          <button type="button" onClick={openFilePicker} disabled={isRunning} className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[10.5px] text-apolo-textSecondary transition hover:border-apolo-accent/40 hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:opacity-40">
            <Paperclip size={10} aria-hidden="true" strokeWidth={2.25} /> Anexar arquivo
          </button>
        </div>
        {uploadError && <p role="alert" className="text-[10.5px] text-apolo-error">{uploadError}</p>}
        {described.length > 0 && (
          <ul className="flex flex-col gap-1">
            {described.map((a) => {
              const Icon = a.kind === 'image' ? ImageIcon : FileText;
              const kindColor = a.inlined ? 'text-emerald-400' : a.kind === 'image' ? 'text-cyan-400' : 'text-apolo-textTertiary';
              return (
                <li key={a.id} className="inline-flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[10.5px] text-apolo-textSecondary">
                  <Icon size={10} aria-hidden="true" strokeWidth={2.25} className={`shrink-0 ${kindColor}`} />
                  <span className="max-w-[14rem] truncate" title={a.name}>{a.name}</span>
                  <span className="text-apolo-textTertiary">{a.sizeKb.toFixed(0)}KB</span>
                  <span className={`text-[9.5px] ${a.inlined ? 'text-apolo-textTertiary' : 'text-apolo-warning'}`}>{a.note}</span>
                  <button type="button" onClick={() => removeAttachment(a.id)} disabled={isRunning} aria-label={`Remover ${a.name}`} className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-error disabled:opacity-40">
                    <X size={9} aria-hidden="true" strokeWidth={2.25} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-col items-stretch gap-2 pt-1 lg:flex-row lg:items-center lg:justify-end">
        {phase === 'done' ? (
          <button type="button" onClick={onReset} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-4 text-sm font-medium text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accent/40 lg:h-auto lg:py-2 lg:text-xs">
            <Sparkles size={12} aria-hidden="true" /> Nova deliberação
          </button>
        ) : isRunning ? (
          <div className="flex items-center justify-end gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-apolo-textSecondary">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Conselho deliberando…
            </span>
            <button type="button" onClick={onCancel} aria-label="Cancelar deliberação" className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 text-xs font-medium text-apolo-textSecondary transition hover:text-apolo-error focus:outline-none focus:ring-2 focus:ring-apolo-error/40 lg:h-auto lg:py-1.5">
              <X size={11} aria-hidden="true" /> Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onConvoke}
            disabled={!canConvoke}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-apolo-accent px-4 text-sm font-semibold text-apolo-bg transition focus:outline-none focus:ring-2 focus:ring-apolo-accentRing disabled:cursor-not-allowed disabled:opacity-40 lg:h-auto lg:py-2 lg:text-xs"
          >
            <Send size={12} aria-hidden="true" /> Convocar Conselho
          </button>
        )}
      </div>
    </section>
  );
}
